import { supabase } from "@/integrations/supabase/client";
import i18n from "@/i18n";
import { tr } from "@/lib/tr";
import { notifyJournalListChanged } from "@/lib/journalListCache";


export type CancellableTable =
  | "sales_invoices"
  | "purchase_invoices"
  | "payment_vouchers"
  | "receipt_vouchers"
  | "journal_entries"
  | "goods_receipts"
  | "goods_issues"
  | "inventory_transfers"
  | "stock_takes"
  | "fixed_assets"
  | "fixed_asset_vouchers"
  | "fixed_asset_disposals"
  | "asset_custody_transfers"
  | "lease_contracts"
  | "lease_payments"
  | "prepaid_expenses"
  | "accrued_expenses"
  | "construction_projects"
  | "cash_custodies"
  | "cash_custody_settlements"
  | "service_receipts"
  | "cash_counts"
  | "sales_invoice_payments"
  | "purchase_invoice_payments";

export interface CancelResult {
  ok: true;
  table: CancellableTable;
  id: string;
  reversal_date: string;
  reversal_journal_entries: string[];
}

/**
 * Named guard codes raised by the unified DB gates (assert_posting_allowed,
 * reverse_document_effects, post_document, cancel_document). They travel inside
 * the pipe payload, e.g. "SD007|PERIOD_CLOSED_MONTH|2026-06".
 * Exported so every document screen shows the same wording.
 */
const DOC_TYPE_LABELS: Record<string, [string, string, string, string]> = {
  sales_invoices: ["فاتورة مبيعات", "sales invoice", "facture de vente", "سیلز انوائس"],
  purchase_invoices: ["فاتورة مشتريات", "purchase invoice", "facture d'achat", "پرچیز انوائس"],
  goods_issues: ["إذن صرف", "goods issue", "bon de sortie", "گڈز ایشو"],
  goods_receipts: ["إذن استلام", "goods receipt", "bon de réception", "گڈز رسید"],
  inventory_transfers: ["تحويل مخزني", "stock transfer", "transfert de stock", "اسٹاک ٹرانسفر"],
  stock_takes: ["جرد مخزني", "stock take", "inventaire", "اسٹاک ٹیک"],
};

/** Human reference from a payload part: an invoice number, a period label, or "table:uuid". */
function guardRef(parts: string[]): string {
  const plain = parts.find((p) => /[A-Z]{2,}-\w|^\d{4}-\d{2}$/.test(p.trim()));
  if (plain) return plain.trim();
  const typed = parts.find((p) => /^[a-z_]+:[0-9a-f-]{8,}$/i.test(p.trim()));
  if (typed) {
    const [table, uuid] = typed.trim().split(":");
    const label = DOC_TYPE_LABELS[table];
    const name = label ? tr(label[0], label[1], label[2], label[3]) : table;
    return `${name} #${uuid.slice(0, 8)}`;
  }
  return "";
}

/** Message for SD044 ENTRY_PERIOD_LOCKED (manual journal entry in a locked or filed period). */
export function entryPeriodLockedMessage(): string {
  return tr(
    "فترة هذا القيد مقفلة ولا يمكن إلغاؤه. لتصحيحه سجّل قيد تسوية في الفترة الحالية.",
    "This entry's period is locked, so it cannot be cancelled. To correct it, record an adjusting entry in the current period.",
    "La période de cette écriture est clôturée, elle ne peut pas être annulée. Pour la corriger, enregistrez une écriture de régularisation sur la période en cours.",
    "اس اندراج کی مدت بند ہے، اس لیے اسے منسوخ نہیں کیا جا سکتا۔ درستگی کے لیے موجودہ مدت میں ایڈجسٹمنٹ اندراج درج کریں۔",
  );
}

export function translateGuardToken(token: string, parts: string[] = []): string | null {
  const ref = guardRef(parts);
  const refAr = ref ? ` (${ref})` : "";

  switch (token) {
    case "ENTRY_PERIOD_LOCKED":
      return entryPeriodLockedMessage();
    case "UNPOST_STRICT_FORBIDDEN":
      return tr(
        "إرجاع القيد إلى مسودة غير متاح في الوضع الصارم. لتصحيحه ألغِ القيد بقيد عكسي.",
        "Returning an entry to draft is not available in strict mode. To correct it, cancel the entry with a reversing entry.",
        "Le retour en brouillon n'est pas disponible en mode strict. Pour corriger, annulez l'écriture par contrepassation.",
        "سخت موڈ میں اندراج کو ڈرافٹ میں واپس کرنا دستیاب نہیں۔ درستگی کے لیے اندراج کو ریورسل سے منسوخ کریں۔",
      );
    case "LEASE_PAYMENT_VIA_CONTRACT":
      return tr(
        "لا يمكن إلغاء دفعة إيجار بمفردها. لتصحيح الجدول، ألغِ العقد كاملاً من شاشة العقد ثم سجّل عقداً صحيحاً.",
        "A single lease payment cannot be cancelled on its own. To correct the schedule, cancel the whole contract from the contract screen, then record a correct one.",
        "Un paiement de bail ne peut pas être annulé seul. Pour corriger l'échéancier, annulez le contrat entier depuis l'écran du contrat, puis enregistrez un contrat correct.",
        "کرایہ کی ایک قسط اکیلے منسوخ نہیں کی جا سکتی۔ شیڈول درست کرنے کے لیے معاہدے کی اسکرین سے پورا معاہدہ منسوخ کریں، پھر درست معاہدہ درج کریں۔",
      );
    case "GI_FROM_STOCK_TAKE":
      return tr(
        "هذا السند صادر من الجرد. لإلغائه ألغِ الجرد نفسه.",
        "This issue was generated by a stock take. To cancel it, cancel the stock take itself.",
        "Ce bon a été généré par un inventaire. Pour l'annuler, annulez l'inventaire lui-même.",
        "یہ واؤچر اسٹاک ٹیک سے بنا ہے۔ اسے منسوخ کرنے کے لیے خود اسٹاک ٹیک منسوخ کریں۔",
      );
    case "FEATURE_DISABLED":
      return tr(
        "هذه الخاصية غير مفعّلة لشركتك حالياً. تواصل مع الدعم لتفعيلها.",
        "This feature is not enabled for your company yet. Contact support to enable it.",
        "Cette fonctionnalité n'est pas encore activée pour votre société. Contactez le support pour l'activer.",
        "یہ سہولت ابھی آپ کی کمپنی کے لیے فعال نہیں۔ فعال کرانے کے لیے سپورٹ سے رابطہ کریں۔",
      );
    case "LEASE_HAS_POSTINGS":
      return tr(
        "لا يمكن إرجاع العقد إلى مسودة لأن له قيوداً مرحّلة. استخدم إلغاء العقد بدلاً من ذلك.",
        "The contract cannot return to draft because it has posted entries. Use contract cancellation instead.",
        "Le contrat ne peut pas revenir en brouillon car il a des écritures validées. Utilisez plutôt l'annulation du contrat.",
        "معاہدہ ڈرافٹ میں واپس نہیں جا سکتا کیونکہ اس کی پوسٹ شدہ انٹریاں موجود ہیں۔ اس کے بجائے معاہدہ منسوخ کریں۔",
      );
    case "FINANCIAL_FIELD":
      return tr(
        "لا يمكن تعديل الحقول المالية لعقد مفعّل. للتصحيح المالي ألغِ العقد وسجّل عقداً جديداً.",
        "Financial fields of an active contract cannot be edited. For a financial correction, cancel the contract and record a new one.",
        "Les champs financiers d'un contrat actif ne peuvent pas être modifiés. Pour une correction financière, annulez le contrat et enregistrez-en un nouveau.",
        "فعال معاہدے کے مالی خانے تبدیل نہیں کیے جا سکتے۔ مالی درستگی کے لیے معاہدہ منسوخ کر کے نیا درج کریں۔",
      );
    case "LEASE_FIELD_LOCKED":
    case "LEASE_EDIT_VIA_FUNCTION":
      return tr(
        "هذا العقد مفعّل، فلا يمكن تعديل بياناته المالية أو حساباته. البيانات الوصفية فقط تُعدّل من شاشة العقد. للتصحيح المالي ألغِ العقد وسجّل عقداً جديداً.",
        "This contract is active, so its financial data and accounts cannot be edited. Only descriptive details can be edited from the contract screen. For a financial correction, cancel the contract and record a new one.",
        "Ce contrat est actif, ses données financières et ses comptes ne peuvent pas être modifiés. Seules les informations descriptives sont modifiables depuis l'écran du contrat. Pour une correction financière, annulez le contrat et enregistrez-en un nouveau.",
        "یہ معاہدہ فعال ہے، اس لیے اس کا مالی ڈیٹا اور اکاؤنٹس تبدیل نہیں کیے جا سکتے۔ صرف وضاحتی تفصیلات معاہدے کی اسکرین سے تبدیل ہو سکتی ہیں۔ مالی درستگی کے لیے معاہدہ منسوخ کر کے نیا درج کریں۔",
      );
    case "LEASE_TERMINATED_LOCKED":
      return tr(
        "هذا العقد منتهٍ ومقفل. للتصحيح استخدم التراجع عن الإنهاء من شاشة العقد.",
        "This contract is terminated and locked. To correct it, undo the termination from the contract screen.",
        "Ce contrat est résilié et verrouillé. Pour le corriger, annulez la résiliation depuis l'écran du contrat.",
        "یہ معاہدہ برخاست اور مقفل ہے۔ درستگی کے لیے معاہدے کی اسکرین سے برخاستگی واپس لیں۔",
      );
    case "LEASE_RECOGNITION_LOCKED":
      return tr(
        "لا يمكن تعديل سطر إثبات المصروف مباشرة. التغييرات تتم فقط عبر إنهاء العقد أو التراجع عنه.",
        "An expense recognition row cannot be edited directly. Changes happen only through terminating the contract or undoing it.",
        "Une ligne de constatation de charge ne peut pas être modifiée directement. Les changements passent uniquement par la résiliation ou son annulation.",
        "اخراجات کے اندراج کی سطر براہ راست تبدیل نہیں ہو سکتی۔ تبدیلی صرف برخاستگی یا اس کی واپسی سے ہوتی ہے۔",
      );
    case "LEASE_TERMINATION_LOCKED":
      return tr(
        "سجل إنهاء العقد محمي ولا يُعدّل أو يُحذف مباشرة.",
        "The lease termination record is protected and cannot be edited or deleted directly.",
        "L'enregistrement de résiliation est protégé et ne peut pas être modifié ou supprimé directement.",
        "برخاستگی کا ریکارڈ محفوظ ہے، اسے براہ راست تبدیل یا حذف نہیں کیا جا سکتا۔",
      );
    case "LEASE_PAYMENT_LOCKED":
      return tr(
        "لا يمكن تعديل أو إضافة أو حذف دفعات عقد مفعّل أو دفعة مرحّلة. تُرحّل الدفعات تلقائياً، وللتصحيح ألغِ العقد.",
        "Payments of an active contract, or a posted payment, cannot be edited, added or deleted. Payments post automatically; to correct them, cancel the contract.",
        "Les échéances d'un contrat actif, ou une échéance validée, ne peuvent pas être modifiées, ajoutées ou supprimées. Elles sont validées automatiquement ; pour corriger, annulez le contrat.",
        "فعال معاہدے کی ادائیگیاں یا پوسٹ شدہ ادائیگی تبدیل، شامل یا حذف نہیں کی جا سکتی۔ ادائیگیاں خود بخود پوسٹ ہوتی ہیں، درستگی کے لیے معاہدہ منسوخ کریں۔",
      );
    case "NO_FISCAL_PERIOD":
      return tr(
        `لا توجد سنة مالية تغطي التاريخ${refAr}. أنشئ السنة المالية أولاً ثم أعد المحاولة.`,
        `No fiscal year covers this date${refAr}. Create the fiscal year first, then retry.`,
        `Aucun exercice ne couvre cette date${refAr}. Créez d'abord l'exercice, puis réessayez.`,
        `اس تاریخ کے لیے کوئی مالی سال موجود نہیں${refAr}۔ پہلے مالی سال بنائیں، پھر دوبارہ کوشش کریں۔`,
      );
    case "BEFORE_OPENING_BALANCE":
      return tr(
        `لا يمكن إنشاء قيد دوري قبل تاريخ الأرصدة الافتتاحية${refAr}. أدخل القيمة السابقة ضمن الرصيد الافتتاحي.`,
        `A recurring entry cannot be posted before the opening-balance date${refAr}. Include the earlier value in opening balances.`,
        `Une écriture périodique ne peut pas être validée avant la date des soldes d'ouverture${refAr}. Incluez la valeur antérieure dans les soldes d'ouverture.`,
        `ابتدائی بیلنس کی تاریخ سے پہلے دورانیہ اندراج پوسٹ نہیں ہو سکتا${refAr}۔ سابقہ رقم ابتدائی بیلنس میں شامل کریں۔`,
      );
    case "OPENING_BALANCE_DATE_MISMATCH":
      return tr(
        `يجب أن يطابق تاريخ قيد الأرصدة الافتتاحية التاريخ المحدد للشركة${refAr}.`,
        `The opening-balance entry date must match the date set for the company${refAr}.`,
        `La date de l'écriture d'ouverture doit correspondre à la date définie pour la société${refAr}.`,
        `ابتدائی بیلنس اندراج کی تاریخ کمپنی کے مقررہ تاریخ سے مماثل ہونی چاہیے${refAr}۔`,
      );
    case "PERIOD_CLOSED_MONTH":
      return tr(
        `الشهر المحاسبي مقفل${refAr}، لذلك لا يمكن تنفيذ العملية على هذا التاريخ.`,
        `The accounting month is closed${refAr}, so the operation cannot be made on this date.`,
        `Le mois comptable est clôturé${refAr} : l'opération est impossible à cette date.`,
        `اکاؤنٹنگ ماہ بند ہے${refAr}، اس لیے اس تاریخ پر کارروائی ممکن نہیں۔`,
      );
    case "PERIOD_CLOSED_FY":
      return tr(
        `السنة المالية مقفلة${refAr}، لذلك لا يمكن تنفيذ العملية.`,
        `The fiscal year is closed${refAr}, so the operation is not allowed.`,
        `L'exercice est clôturé${refAr} : l'opération n'est pas autorisée.`,
        `مالی سال بند ہے${refAr}، اس لیے کارروائی کی اجازت نہیں۔`,
      );
    case "PERIOD_CLOSED_TAX":
      return tr(
        `الفترة الضريبية مقفلة${refAr} وتم ترحيل إقرارها، لذلك لا يمكن تنفيذ العملية.`,
        `The tax period is closed${refAr} and its return is posted, so the operation is not allowed.`,
        `La période de TVA est clôturée${refAr} et sa déclaration est validée : l'opération n'est pas autorisée.`,
        `ٹیکس مدت بند ہے${refAr} اور اس کی ریٹرن پوسٹ ہو چکی ہے، اس لیے کارروائی ممکن نہیں۔`,
      );
    case "INVENTORY_CONSUMED":
      return tr(
        `لا يمكن تنفيذ العملية لأن المخزون استُهلك في مستند لاحق${refAr}. ألغِ المستند اللاحق أولاً.`,
        `Not allowed: the stock was consumed by a later document${refAr}. Cancel that document first.`,
        `Impossible : le stock a été consommé par un document ultérieur${refAr}. Annulez d'abord ce document.`,
        `ممکن نہیں: اسٹاک بعد کی دستاویز${refAr} میں استعمال ہو چکا ہے۔ پہلے وہ دستاویز منسوخ کریں۔`,
      );
    case "PENDING_POSTINGS":
      return tr(
        `توجد عمليات ترحيل دورية فاشلة لم تُعالج${refAr}. عالِجها من تنبيه لوحة المعلومات ثم أعد المحاولة.`,
        `There are unresolved failed periodic postings${refAr}. Clear them from the dashboard alert, then retry.`,
        `Des écritures périodiques en échec ne sont pas résolues${refAr}. Traitez-les depuis l'alerte du tableau de bord, puis réessayez.`,
        `کچھ ناکام دورانیہ اندراجات حل نہیں ہوئے${refAr}۔ ڈیش بورڈ الرٹ سے حل کریں، پھر دوبارہ کوشش کریں۔`,
      );
    case "NO_INVENTORY_ACCOUNT":
      return tr(
        "لا يمكن ترحيل مستند يحتوي أصنافاً مخزنية قبل تحديد حساب المخزون الافتراضي في إعدادات الشركة.",
        "A document with stock items cannot be posted before a default inventory account is set in the company settings.",
        "Un document contenant des articles stockés ne peut pas être validé avant de définir un compte de stock par défaut dans les paramètres de la société.",
        "اسٹاک اشیاء والی دستاویز کمپنی سیٹنگز میں ڈیفالٹ انوینٹری اکاؤنٹ مقرر کرنے سے پہلے پوسٹ نہیں کی جا سکتی۔",
      );
    case "NO_ROUNDING_ACCOUNT":
      return tr(
        "توجد فروق هللات في القيد، ولم يُحدَّد حساب فروق التقريب في إعدادات الشركة. حدِّده ثم أعد المحاولة.",
        "The entry has a fractional rounding difference, but no rounding variance account is set in the company settings. Set it, then retry.",
        "L'écriture présente un écart d'arrondi, mais aucun compte d'écart d'arrondi n'est défini dans les paramètres de la société. Définissez-le, puis réessayez.",
        "اندراج میں معمولی راؤنڈنگ فرق ہے، مگر کمپنی سیٹنگز میں راؤنڈنگ فرق اکاؤنٹ مقرر نہیں۔ اسے مقرر کریں، پھر کوشش کریں۔",
      );

    case "UNBALANCED_JOURNAL":
      return tr(
        `القيد الناتج غير متوازن (مدين ${parts[0] ?? "?"} / دائن ${parts[1] ?? "?"})، لذلك لم يُحفظ أي تغيير.`,
        `The resulting journal entry is unbalanced (debit ${parts[0] ?? "?"} / credit ${parts[1] ?? "?"}), so nothing was saved.`,
        `L'écriture obtenue n'est pas équilibrée (débit ${parts[0] ?? "?"} / crédit ${parts[1] ?? "?"}) : rien n'a été enregistré.`,
        `نتیجہ اندراج غیر متوازن ہے (ڈیبٹ ${parts[0] ?? "?"} / کریڈٹ ${parts[1] ?? "?"})، اس لیے کچھ محفوظ نہیں ہوا۔`,
      );
    case "NO_JOURNAL_CREATED":
    case "JOURNAL_NOT_POSTED":
      return tr(
        "لم يُنشأ قيد محاسبي لهذه العملية، لذلك أُلغيت بالكامل ولم يُحفظ أي تغيير.",
        "No journal entry was created for this operation, so it was rolled back entirely.",
        "Aucune écriture comptable n'a été créée : l'opération a été entièrement annulée.",
        "اس کارروائی کے لیے کوئی اکاؤنٹنگ اندراج نہیں بنا، اس لیے سب کچھ واپس کر دیا گیا۔",
      );
    case "ALREADY_POSTED":
      return tr(
        `المستند مرحّل بالفعل${refAr}.`,
        `The document is already posted${refAr}.`,
        `Le document est déjà validé${refAr}.`,
        `دستاویز پہلے ہی پوسٹ شدہ ہے${refAr}۔`,
      );
    case "EMPTY_DOCUMENT":
      return tr(
        "لا يمكن ترحيل مستند بدون سطور أو بقيمة صفر.",
        "A document with no lines or a zero total cannot be posted.",
        "Un document sans lignes ou d'un total nul ne peut pas être validé.",
        "بغیر لائنوں یا صفر رقم والی دستاویز پوسٹ نہیں کی جا سکتی۔",
      );
    case "PERMISSION_DENIED":
    case "UNAUTHORIZED":
      return tr(
        "لا تملك صلاحية تنفيذ هذه العملية.",
        "You do not have permission to perform this operation.",
        "Vous n'avez pas la permission d'effectuer cette opération.",
        "آپ کو یہ کارروائی کرنے کی اجازت نہیں ہے۔",
      );
    default:
      return null;
  }
}

/**
 * Turn a Postgres error message from cancel_document (which uses coded
 * pipe-delimited payloads like "SD005|<id>|<invoice_no>") into a localised
 * user-facing string.
 */
/**
 * Purchase invoice / goods receipt cancel refused because the received stock
 * was already issued (SD023 INVENTORY_CONSUMED|<doc no>). Names the consuming
 * document and points to a vendor debit note as the correction route.
 */
export function purchaseStockConsumedMessage(message: string): string {
  const after = (message.split("INVENTORY_CONSUMED|")[1] || "").split("|")[0].trim();
  const doc = after && after !== "unknown" ? after : "";
  const ref = doc ? ` (${doc})` : "";
  return tr(
    `لا يمكن الإلغاء لأن المخزون المستلم صُرف في المستند${ref}. للتصحيح، أنشئ إشعاراً مديناً للمورد من شاشة فاتورة الشراء، بدل إلغاء مستند الصرف.`,
    `Cannot cancel: the received stock was already issued by document${ref}. To correct it, create a vendor debit note from the purchase invoice screen instead of cancelling the issuing document.`,
    `Annulation impossible : le stock reçu a déjà été sorti par le document${ref}. Pour corriger, créez une note de débit fournisseur depuis l'écran de la facture d'achat, au lieu d'annuler le document de sortie.`,
    `منسوخی ممکن نہیں: موصول شدہ اسٹاک دستاویز${ref} میں جاری ہو چکا ہے۔ درستگی کے لیے خریداری انوائس کی اسکرین سے سپلائر کے لیے ڈیبٹ نوٹ بنائیں، جاری کرنے والی دستاویز منسوخ نہ کریں۔`,
  );
}

export function translateCancelError(message: string): string {
  const t = i18n.t.bind(i18n);
  // Extract the SD code even when Postgres prefixes with a class label.
  const m = /SD0\d{2}/.exec(message || "");
  if (!m) return message || t("common.cancelErr_SD010");
  const code = m[0];
  const rest = message.slice(message.indexOf(code) + code.length).replace(/^\|/, "");
  const parts = rest.split("|");

  // Named guard tokens win over the numeric code: they carry the precise reason.
  for (let i = 0; i < parts.length; i++) {
    const named = translateGuardToken(parts[i].trim(), parts.slice(i + 1));
    if (named) return named;
  }


  switch (code) {
    case "SD001": return t("common.cancelErr_SD001");
    case "SD002": return t("common.cancelErr_SD002", { status: parts[0] || "" });
    case "SD003": return t("common.cancelErr_SD003");
    case "SD004": return t("common.cancelErr_SD004", { page: parts[0] || "" });
    case "SD005": return t("common.cancelErr_SD005", { invoice: parts[1] || "" });
    case "SD006": {
      const src = (parts[1] || "").trim();
      return t("common.cancelErr_SD006", { source: src ? t(`journal.sources.${src}`, src) : "" });
    }
    case "SD007": {
      // rest layout: level|label|date  (level = "year" | "subperiod")
      const level = parts[0];
      const label = parts[1] || "";
      const date = parts[2] || "";
      if (level === "year") return t("common.cancelErr_SD007_year", { label, date });
      return t("common.cancelErr_SD007_subperiod", { label, date });
    }
    case "SD008": return t("common.cancelErr_SD008");
    case "SD009": return t("common.cancelErr_SD009");
    case "SD012": return t("common.cancelErr_SD012");
    case "SD021": return t("common.cancelErr_SD021", { count: parts[1] || "" });
    case "SD011":
    case "SD023": return translateGuardToken("INVENTORY_CONSUMED", parts) as string;
    case "SD024": return translateGuardToken("NO_JOURNAL_CREATED", parts) as string;
    case "SD026": return translateGuardToken("PENDING_POSTINGS", parts) as string;
    case "SD030": return translateGuardToken("UNBALANCED_JOURNAL", parts) as string;
    case "SD031": return translateGuardToken("ALREADY_POSTED", parts) as string;
    case "SD032": return translateGuardToken("EMPTY_DOCUMENT", parts) as string;
    case "SD033": return translateGuardToken("NO_INVENTORY_ACCOUNT", parts) as string;
    case "SD034": return translateGuardToken("NO_ROUNDING_ACCOUNT", parts) as string;

    case "SD010":
    default:      return t("common.cancelErr_SD010");


  }
}

/**
 * Cancel a posted accounting document atomically.
 * On success, returns the reversal metadata (IDs of newly created reversing JEs).
 * On failure, throws with a localised message.
 */
export async function cancelDocument(
  table: CancellableTable,
  id: string,
  reason: string
): Promise<CancelResult> {
  const trimmed = (reason || "").trim();
  if (trimmed.length < 5) {
    throw new Error(i18n.t("common.cancelErr_SD008"));
  }
  const { data, error } = await (supabase.rpc as any)("cancel_document", {
    p_table: table,
    p_id: id,
    p_reason: trimmed,
  });
  if (error) {
    const msg = error.message || "";
    if (
      (table === "purchase_invoices" || table === "goods_receipts") &&
      (msg.includes("INVENTORY_CONSUMED") || (error as { code?: string }).code === "SD023")
    ) {
      throw new Error(purchaseStockConsumedMessage(msg));
    }
    throw new Error(translateCancelError(msg));
  }
  notifyJournalListChanged();
  return data as CancelResult;
}
